// Work Orders Frontend Functions Library
// These functions are self-contained and use _shared helpers directly (like Hoomie)

import {
  getDocumentById,
  getCollection,
  setDocById,
  addDocToCollection,
  updateDocById,
  deleteDocByCollectionAndId,
} from '../_shared/firestore/index.js';
import { WorkOrderModel } from '@cmms/models';
import { createCMMSView } from '../views/views.js';
import { getAuth } from 'firebase/auth';

const COLLECTION_NAME = 'work_orders';

// Helper function to get current user's company ID
async function getCurrentUserCompanyId(db) {
  const auth = getAuth(db.app);
  const currentUser = auth.currentUser;
  
  if (!currentUser) {
    throw new Error('User not authenticated');
  }

  const userDoc = await getDocumentById(db, 'users', currentUser.uid);
  
  if (!userDoc || !userDoc.company_id) {
    throw new Error('User company not found');
  }

  return userDoc.company_id;
}

// Frontend function: Get all work orders
async function getWorkOrders(db) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME);

    const workOrders = docs.map((doc) => {
      try {
        // Map Firestore fields to WorkOrderModel fields
        const mappedData = {
          wo_id: doc.wo_id || doc.id || doc.$id,
          company_id: doc.company_id || '',
          asset_id: doc.asset_id || '',
          description: doc.description || '',
          priority: doc.priority || 'Medium',
          assigned_to: doc.assigned_to || '',
          status: doc.status || (doc.status_cat === 1 ? 'Open' : doc.status_cat === 2 ? 'In Progress' : doc.status_cat === 3 ? 'Completed' : doc.status_cat === 4 ? 'Awaiting Parts' : 'Open'),
          created_at: doc.created_at || Date.now(),
          updated_at: doc.updated_at || Date.now(),
          // Timing fields (pass through if present)
          active_work_ms: doc.active_work_ms ?? 0,
          current_started_at: doc.current_started_at ?? null,
          completed_at: doc.completed_at ?? null,
          // PM-specific fields
          is_pm: doc.is_pm ?? false,
          pm_template_id: doc.pm_template_id ?? null,
          pm_due_at: doc.pm_due_at ?? null,
          pm_cycle_start_at: doc.pm_cycle_start_at ?? null,
          pm_cycle_id: doc.pm_cycle_id ?? null,
          grace_days: doc.grace_days ?? null,
          checklist: doc.checklist ?? [],
          parts: doc.parts ?? [],
        };

        const workOrder = new WorkOrderModel(mappedData);
        return { id: doc.$id, model: workOrder };
      } catch (modelError) {
        console.error(
          'Error creating WorkOrderModel for doc:',
          doc,
          modelError
        );
        // Return raw data if model creation fails
        return {
          id: doc.$id,
          model: {
            wo_id: doc.wo_id || doc.id || doc.$id,
            description: doc.description || 'No description',
            status: doc.status || 'Unknown',
            priority: doc.priority || 'Medium',
            assigned_to: doc.assigned_to || 'Unassigned',
            asset_id: doc.asset_id || '',
            due_date: doc.due_date || '',
            created_at: doc.created_at || '',
            company_id: doc.company_id || '',
          },
        };
      }
    });

    return createCMMSView({ data: workOrders });
  } catch (error) {
    console.error('Error in getWorkOrders:', error);
    return createCMMSView({ error });
  }
}

async function getWorkOrdersByCompany(db, companyId) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, {
      key: 'company_id',
      operator: '==',
      value: companyId,
    });

    // Filter out PM work orders - only return regular work orders
    const regularWorkOrders = docs.filter(doc => !doc.is_pm || doc.is_pm === false);

    const workOrders = regularWorkOrders.map((doc) => {
      try {
        // Map Firestore fields to WorkOrderModel fields (same as getWorkOrders)
        const mappedData = {
          wo_id: doc.wo_id || doc.id || doc.$id,
          company_id: doc.company_id || companyId,
          asset_id: doc.asset_id || '',
          description: doc.description || '',
          priority_cat: doc.priority_cat || doc.priority || 2, // Default to Medium
          status_cat: doc.status_cat || doc.status || 1, // Default to Open
          status: doc.status || (doc.status_cat === 1 ? 'Open' : doc.status_cat === 2 ? 'In Progress' : doc.status_cat === 3 ? 'Completed' : doc.status_cat === 4 ? 'Awaiting Parts' : 'Open'),
          assigned_to: doc.assigned_to || '',
          created_at: doc.created_at || Date.now(),
          updated_at: doc.updated_at || Date.now(),
          // Timing fields (pass through if present)
          active_work_ms: doc.active_work_ms ?? 0,
          current_started_at: doc.current_started_at ?? null,
          completed_at: doc.completed_at ?? null,
          // PM-specific fields
          is_pm: doc.is_pm ?? false,
          pm_template_id: doc.pm_template_id ?? null,
          pm_due_at: doc.pm_due_at ?? null,
          pm_cycle_start_at: doc.pm_cycle_start_at ?? null,
          pm_cycle_id: doc.pm_cycle_id ?? null,
          grace_days: doc.grace_days ?? null,
          checklist: doc.checklist ?? [],
          parts: doc.parts ?? [],
        };

        const workOrder = new WorkOrderModel(mappedData);
        return { id: doc.$id, model: workOrder };
      } catch (modelError) {
        // Return raw data if model creation fails
        return {
          id: doc.$id,
          model: {
            wo_id: doc.wo_id || doc.id || doc.$id,
            description: doc.description || 'No description',
            status_cat: doc.status_cat || doc.status || 1,
            priority_cat: doc.priority_cat || doc.priority || 2,
            assigned_to: doc.assigned_to || '',
            created_by: doc.created_by || '',
            asset_id: doc.asset_id || '',
            created_at: doc.created_at || Date.now(),
            updated_at: doc.updated_at || Date.now(),
            company_id: doc.company_id || companyId,
          },
        };
      }
    });

    return createCMMSView({ data: workOrders });
  } catch (error) {
    return createCMMSView({ error });
  }
}

async function getPMWorkOrdersByCompany(db, companyId) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, {
      key: 'company_id',
      operator: '==',
      value: companyId,
    });

    // Filter for PM work orders only - only return PM work orders
    const pmWorkOrders = docs.filter(doc => doc.is_pm === true);

    const workOrders = pmWorkOrders.map((doc) => {
      try {
        // Map Firestore fields to WorkOrderModel fields (same as getWorkOrders)
        const mappedData = {
          wo_id: doc.wo_id || doc.id || doc.$id,
          company_id: doc.company_id || companyId,
          asset_id: doc.asset_id || '',
          description: doc.description || '',
          priority_cat: doc.priority_cat || doc.priority || 2, // Default to Medium
          status_cat: doc.status_cat || doc.status || 1, // Default to Open
          status: doc.status || (doc.status_cat === 1 ? 'Open' : doc.status_cat === 2 ? 'In Progress' : doc.status_cat === 3 ? 'Completed' : doc.status_cat === 4 ? 'Awaiting Parts' : 'Open'),
          assigned_to: doc.assigned_to || '',
          created_at: doc.created_at || Date.now(),
          updated_at: doc.updated_at || Date.now(),
          // Timing fields (pass through if present)
          active_work_ms: doc.active_work_ms ?? 0,
          current_started_at: doc.current_started_at ?? null,
          completed_at: doc.completed_at ?? null,
          // PM-specific fields
          is_pm: doc.is_pm ?? false,
          pm_template_id: doc.pm_template_id ?? null,
          pm_due_at: doc.pm_due_at ?? null,
          pm_cycle_start_at: doc.pm_cycle_start_at ?? null,
          pm_cycle_id: doc.pm_cycle_id ?? null,
          grace_days: doc.grace_days ?? null,
          checklist: doc.checklist ?? [],
          parts: doc.parts ?? [],
        };

        const workOrder = new WorkOrderModel(mappedData);
        return { id: doc.$id, model: workOrder };
      } catch (modelError) {
        // Return raw data if model creation fails
        return {
          id: doc.$id,
          model: {
            wo_id: doc.wo_id || doc.id || doc.$id,
            description: doc.description || 'No description',
            status_cat: doc.status_cat || doc.status || 1,
            priority_cat: doc.priority_cat || doc.priority || 2,
            asset_id: doc.asset_id || '',
            assigned_to: doc.assigned_to || '',
            company_id: companyId,
            created_at: doc.created_at || Date.now(),
            updated_at: doc.updated_at || Date.now(),
            // PM-specific fields
            is_pm: doc.is_pm ?? false,
            pm_template_id: doc.pm_template_id ?? null,
            pm_due_at: doc.pm_due_at ?? null,
            pm_cycle_start_at: doc.pm_cycle_start_at ?? null,
            pm_cycle_id: doc.pm_cycle_id ?? null,
            grace_days: doc.grace_days ?? null,
            checklist: doc.checklist ?? [],
            parts: doc.parts ?? [],
          }
        };
      }
    });

    return createCMMSView({ data: workOrders });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get work orders created by a specific user (for Shop Floor)
async function getWorkOrdersByCreatedBy(db, companyId, userId) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, {
      key: 'company_id',
      operator: '==',
      value: companyId,
    });

    // Filter for work orders created by this user, excluding PM work orders
    const userWorkOrders = docs.filter(doc => 
      doc.created_by === userId && (!doc.is_pm || doc.is_pm === false)
    );

    const workOrders = userWorkOrders.map((doc) => {
      try {
        // Map Firestore fields to WorkOrderModel fields
        const mappedData = {
          wo_id: doc.wo_id || doc.id || doc.$id,
          company_id: doc.company_id || companyId,
          asset_id: doc.asset_id || '',
          description: doc.description || '',
          priority_cat: doc.priority_cat || doc.priority || 2,
          status_cat: doc.status_cat || doc.status || 1,
          status: doc.status || (doc.status_cat === 1 ? 'Open' : doc.status_cat === 2 ? 'In Progress' : doc.status_cat === 3 ? 'Completed' : doc.status_cat === 4 ? 'Awaiting Parts' : 'Open'),
          assigned_to: doc.assigned_to || '',
          created_by: doc.created_by || '',
          created_at: doc.created_at || Date.now(),
          updated_at: doc.updated_at || Date.now(),
          active_work_ms: doc.active_work_ms ?? 0,
          current_started_at: doc.current_started_at ?? null,
          completed_at: doc.completed_at ?? null,
          is_pm: doc.is_pm ?? false,
          pm_template_id: doc.pm_template_id ?? null,
          pm_due_at: doc.pm_due_at ?? null,
          pm_cycle_start_at: doc.pm_cycle_start_at ?? null,
          pm_cycle_id: doc.pm_cycle_id ?? null,
          grace_days: doc.grace_days ?? null,
          checklist: doc.checklist ?? [],
          parts: doc.parts ?? [],
        };

        const workOrder = new WorkOrderModel(mappedData);
        return { id: doc.$id, model: workOrder };
      } catch (modelError) {
        // Return raw data if model creation fails
        return {
          id: doc.$id,
          model: {
            wo_id: doc.wo_id || doc.id || doc.$id,
            description: doc.description || 'No description',
            status_cat: doc.status_cat || doc.status || 1,
            priority_cat: doc.priority_cat || doc.priority || 2,
            assigned_to: doc.assigned_to || '',
            created_by: doc.created_by || '',
            asset_id: doc.asset_id || '',
            created_at: doc.created_at || Date.now(),
            updated_at: doc.updated_at || Date.now(),
            company_id: doc.company_id || companyId,
          },
        };
      }
    });

    return createCMMSView({ data: workOrders });
  } catch (error) {
    console.error('Error in getWorkOrdersByCreatedBy:', error);
    return createCMMSView({ error });
  }
}

// Frontend function: Get work order by ID
async function getWorkOrderById(db, workOrderId) {
  try {
    // Get current user's company ID
    const companyId = await getCurrentUserCompanyId(db);

    const doc = await getDocumentById(db, COLLECTION_NAME, workOrderId);
    if (!doc) return createCMMSView({ error: 'Work order not found' });
    
    // Check if work order belongs to user's company
    if (doc.company_id !== companyId) {
      return createCMMSView({ error: 'Access denied: Work order not found' });
    }
    
    const workOrder = new WorkOrderModel(doc);
    return createCMMSView({ data: { id: doc.$id, model: workOrder } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Create new work order
async function createWorkOrder(db, workOrderData) {
  try {
    // Get current user's company ID and user ID
    const companyId = await getCurrentUserCompanyId(db);
    const auth = getAuth(db.app);
    const currentUser = auth.currentUser;
    const userId = currentUser ? currentUser.uid : '';
    
    // Ensure work order is created for user's company with created_by
    const workOrderWithCompany = {
      ...workOrderData,
      company_id: companyId,
      created_by: userId, // Add the current user as creator
      // Convert null assigned_to to empty string if needed
      assigned_to: workOrderData.assigned_to || ''
    };
    // Diagnostics: log payload before model normalization
    console.log('[Diagnostics] Creating WO payload (frontend API):', workOrderWithCompany);
    
    const workOrder = new WorkOrderModel(workOrderWithCompany);
    const docRef = await addDocToCollection(
      db,
      COLLECTION_NAME,
      workOrder.toFirestore()
    );
    console.log('docRef', docRef);
    return createCMMSView({ data: docRef });
  } catch (error) {
    console.error('Error in createWorkOrder:', error);
    return createCMMSView({ error });
  }
}

// Frontend function: Update work order
async function updateWorkOrder(db, workOrderId, updateData) {
  try {
    // Get current user's company ID
    const companyId = await getCurrentUserCompanyId(db);
    
    // First get the existing work order to preserve all data
    const existingDoc = await getDocumentById(db, COLLECTION_NAME, workOrderId);
    if (!existingDoc) {
      return createCMMSView({ error: 'Work order not found' });
    }
    
    // Check if work order belongs to user's company
    if (existingDoc.company_id !== companyId) {
      return createCMMSView({ error: 'Access denied: Work order not found' });
    }

    // Merge existing data with update data
    const mergedData = {
      ...existingDoc,
      ...updateData,
      updated_at: Date.now() // Always update the timestamp
    };

    // Normalize only the back-compat strings, keep all other fields intact (e.g., timing)
    const model = new WorkOrderModel(mergedData);
    const finalUpdate = {
      ...mergedData,
      status: model.status,
      priority: model.priority,
    };

    // Diagnostics: log update payload
    console.log('[Diagnostics] Updating WO payload (frontend API):', { id: workOrderId, update: finalUpdate });
    if (!('assigned_to' in finalUpdate) || !finalUpdate.assigned_to) {
      console.warn('[Diagnostics] assigned_to missing or empty in update payload (frontend API)', { id: workOrderId, update: finalUpdate });
    }
    if (!('asset_id' in finalUpdate) || !finalUpdate.asset_id) {
      console.warn('[Diagnostics] asset_id missing or empty in update payload (frontend API)', { id: workOrderId, update: finalUpdate });
    }

    await updateDocById(
      db,
      COLLECTION_NAME,
      workOrderId,
      finalUpdate
    );

    return createCMMSView({ data: { id: workOrderId, ...finalUpdate } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Delete work order
async function deleteWorkOrder(db, workOrderId) {
  try {
    // Get current user's company ID
    const companyId = await getCurrentUserCompanyId(db);
    
    // First get the existing work order to check company access
    const existingDoc = await getDocumentById(db, COLLECTION_NAME, workOrderId);
    if (!existingDoc) {
      return createCMMSView({ error: 'Work order not found' });
    }
    
    // Check if work order belongs to user's company
    if (existingDoc.company_id !== companyId) {
      return createCMMSView({ error: 'Access denied: Work order not found' });
    }
    
    await deleteDocByCollectionAndId(db, COLLECTION_NAME, workOrderId);
    return createCMMSView({ data: { id: workOrderId, deleted: true } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get work orders by status
async function getWorkOrdersByStatus(db, status) {
  try {
    // Get current user's company ID
    const companyId = await getCurrentUserCompanyId(db);

    // Query with both status and company_id filters
    const docs = await getCollection(db, COLLECTION_NAME, [
      { key: 'status', operator: '==', value: status },
      { key: 'company_id', operator: '==', value: companyId }
    ]);

    const workOrders = docs.map((doc) => {
      return { id: doc.$id, model: new WorkOrderModel(doc) };
    });

    return createCMMSView({ data: workOrders });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get work orders by priority
async function getWorkOrdersByPriority(db, priority) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, {
      key: 'priority',
      operator: '==',
      value: priority,
    });

    const workOrders = docs.map((doc) => {
      return { id: doc.$id, model: new WorkOrderModel(doc) };
    });

    return createCMMSView({ data: workOrders });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get work orders by assigned user
async function getWorkOrdersByAssignedUser(db, assignedTo) {
  try {
    // Get current user's company ID
    const companyId = await getCurrentUserCompanyId(db);

    // Query with both assigned_to and company_id filters
    const docs = await getCollection(db, COLLECTION_NAME, [
      { key: 'assigned_to', operator: '==', value: assignedTo },
      { key: 'company_id', operator: '==', value: companyId }
    ]);

    const workOrders = docs.map((doc) => {
      return { id: doc.$id, model: new WorkOrderModel(doc) };
    });

    return createCMMSView({ data: workOrders });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get work orders by asset
async function getWorkOrdersByAsset(db, assetId) {
  try {
    // Get current user's company ID
    const companyId = await getCurrentUserCompanyId(db);

    // Query with both asset_id and company_id filters
    const docs = await getCollection(db, COLLECTION_NAME, [
      { key: 'asset_id', operator: '==', value: assetId },
      { key: 'company_id', operator: '==', value: companyId }
    ]);

    const workOrders = docs.map((doc) => {
      return { id: doc.$id, model: new WorkOrderModel(doc) };
    });

    return createCMMSView({ data: workOrders });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get open work orders
async function getOpenWorkOrders(db) {
  return await getWorkOrdersByStatus(db, 'open');
}

// Frontend function: Get in-progress work orders
async function getInProgressWorkOrders(db) {
  return await getWorkOrdersByStatus(db, 'in_progress');
}

// Frontend function: Get completed work orders
async function getCompletedWorkOrders(db) {
  return await getWorkOrdersByStatus(db, 'completed');
}

// Frontend function: Get closed work orders
async function getClosedWorkOrders(db) {
  return await getWorkOrdersByStatus(db, 'closed');
}

// Frontend function: Get critical work orders
async function getCriticalWorkOrders(db) {
  return await getWorkOrdersByPriority(db, 'critical');
}

// Frontend function: Get high priority work orders
async function getHighPriorityWorkOrders(db) {
  return await getWorkOrdersByPriority(db, 'high');
}

// Export frontend functions
export {
  getWorkOrders,
  getWorkOrderById,
  getWorkOrdersByCompany,
  getPMWorkOrdersByCompany,
  getWorkOrdersByCreatedBy,
  createWorkOrder,
  updateWorkOrder,
  deleteWorkOrder,
  getWorkOrdersByStatus,
  getWorkOrdersByPriority,
  getWorkOrdersByAssignedUser,
  getWorkOrdersByAsset,
  getOpenWorkOrders,
  getInProgressWorkOrders,
  getCompletedWorkOrders,
  getClosedWorkOrders,
  getCriticalWorkOrders,
  getHighPriorityWorkOrders,
};
